import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  CogIcon,
  CheckCircleIcon,
  XCircleIcon,
  WrenchIcon,
  UserGroupIcon,
  CubeIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';
import { cleanDataForAPI } from '../../utils/data-cleanup';

interface WorkCenter {
  id: string;
  code: string;
  name: string;
  type: string;
  plantId: string;
  plant?: {
    id: string;
    name: string;
    code: string;
  };
  costCenterId?: string;
  costCenter?: {
    id: string;
    name: string;
    code: string;
  };
  description?: string;
  location?: string;
  status: string;
  capacity: number;
  standardHoursPerDay: number;
  efficiencyPercent: number;
  utilizationPercent: number;
  hourlyRate: number;
  setupCost: number;
  overheadRate: number;
  specifications?: Record<string, any>;
  qualifications?: string[];
  availableFrom?: string;
  availableTo?: string;
  workingDays?: number[];
  isActive: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

interface Plant {
  id: string;
  code: string;
  name: string;
}

interface CostCenter {
  id: string;
  code: string;
  name: string;
}

const WorkCenterType = {
  MACHINE: 'machine',
  LABOR: 'labor',
  ASSEMBLY: 'assembly',
  QUALITY: 'quality',
  PACKAGING: 'packaging',
  MAINTENANCE: 'maintenance',
};

const WorkCenterStatus = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  MAINTENANCE: 'maintenance',
  BREAKDOWN: 'breakdown',
};

const WorkCenterManagement: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedWorkCenter, setSelectedWorkCenter] = useState<WorkCenter | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPlant, setFilterPlant] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  
  const queryClient = useQueryClient();

  // Fetch work centers
  const { data: workCentersData, isLoading } = useQuery({
    queryKey: ['workCenters', searchTerm, filterPlant, filterType, filterStatus],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filterPlant) params.append('plantId', filterPlant);
      if (filterType) params.append('type', filterType);
      if (filterStatus) params.append('status', filterStatus);
      
      const response = await api.get(`/master-data/work-centers?${params}`);
      return response.data;
    },
  });

  // Fetch plants for dropdown
  const { data: plantsData } = useQuery({
    queryKey: ['plants'],
    queryFn: async () => {
      const response = await api.get('/master-data/plants');
      return response.data;
    },
  });

  // Fetch cost centers for dropdown
  const { data: costCentersData } = useQuery({
    queryKey: ['costCenters'],
    queryFn: async () => {
      const response = await api.get('/master-data/cost-centers');
      return response.data;
    },
  });

  // Create/Update work center
  const createUpdateMutation = useMutation({
    mutationFn: async (data: any) => {
      // Clean the data to remove readonly fields
      const cleanData = cleanDataForAPI(data);
      
      // Remove additional readonly fields specific to work centers
      delete cleanData.id;
      delete cleanData.createdAt;
      delete cleanData.updatedAt;
      delete cleanData.createdBy;
      delete cleanData.updatedBy;
      delete cleanData.plant;
      delete cleanData.costCenter;
      
      if (selectedWorkCenter) {
        return api.patch(`/master-data/work-centers/${selectedWorkCenter.id}`, cleanData);
      }
      return api.post('/master-data/work-centers', cleanData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workCenters'] });
      toast.success(selectedWorkCenter ? 'Work center updated successfully' : 'Work center created successfully');
      closeModal();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Operation failed');
    },
  });

  // Delete work center
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return api.delete(`/master-data/work-centers/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workCenters'] });
      toast.success('Work center deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Delete failed');
    },
  });

  // Toggle status
  const toggleStatusMutation = useMutation({
    mutationFn: async (id: string) => {
      return api.patch(`/master-data/work-centers/${id}/toggle-status`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workCenters'] });
      toast.success('Status updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Status update failed');
    },
  });

  const openModal = (workCenter?: WorkCenter) => {
    setSelectedWorkCenter(workCenter || null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedWorkCenter(null);
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case WorkCenterType.MACHINE:
        return <CogIcon className="h-5 w-5" />;
      case WorkCenterType.LABOR:
        return <UserGroupIcon className="h-5 w-5" />;
      case WorkCenterType.ASSEMBLY:
        return <CubeIcon className="h-5 w-5" />;
      case WorkCenterType.MAINTENANCE:
        return <WrenchIcon className="h-5 w-5" />;
      default:
        return <CogIcon className="h-5 w-5" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      [WorkCenterStatus.ACTIVE]: 'bg-green-100 text-green-800',
      [WorkCenterStatus.INACTIVE]: 'bg-gray-100 text-gray-800',
      [WorkCenterStatus.MAINTENANCE]: 'bg-yellow-100 text-yellow-800',
      [WorkCenterStatus.BREAKDOWN]: 'bg-red-100 text-red-800',
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusConfig[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Work Center Management</h1>
        <p className="text-gray-600">Manage manufacturing and service work centers</p>
      </div>

      {/* Filters and Actions */}
      <Card className="mb-6">
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search work centers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            <select
              value={filterPlant}
              onChange={(e) => setFilterPlant(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Plants</option>
              {plantsData?.data?.map((plant: Plant) => (
                <option key={plant.id} value={plant.id}>
                  {plant.name}
                </option>
              ))}
            </select>

            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Types</option>
              {Object.entries(WorkCenterType).map(([key, value]) => (
                <option key={value} value={value}>
                  {key.charAt(0) + key.slice(1).toLowerCase()}
                </option>
              ))}
            </select>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Status</option>
              {Object.entries(WorkCenterStatus).map(([key, value]) => (
                <option key={value} value={value}>
                  {key.charAt(0) + key.slice(1).toLowerCase()}
                </option>
              ))}
            </select>

            <Button onClick={() => openModal()} className="flex items-center gap-2">
              <PlusIcon className="h-5 w-5" />
              Add Work Center
            </Button>
          </div>
        </div>
      </Card>

      {/* Work Centers Grid */}
      {isLoading ? (
        <div className="text-center py-8">Loading...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {workCentersData?.data?.map((workCenter: WorkCenter) => (
            <Card key={workCenter.id} className="hover:shadow-lg transition-shadow">
              <div className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {getTypeIcon(workCenter.type)}
                    <div>
                      <h3 className="font-semibold text-gray-900">{workCenter.name}</h3>
                      <p className="text-sm text-gray-500">{workCenter.code}</p>
                    </div>
                  </div>
                  {getStatusBadge(workCenter.status)}
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Plant:</span>
                    <span className="font-medium">{workCenter.plant?.name}</span>
                  </div>
                  {workCenter.costCenter && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Cost Center:</span>
                      <span className="font-medium">{workCenter.costCenter?.name}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Capacity:</span>
                    <span className="font-medium">{workCenter.capacity} units</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Efficiency:</span>
                    <span className="font-medium">{workCenter.efficiencyPercent}%</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Utilization:</span>
                    <span className="font-medium">{workCenter.utilizationPercent}%</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Hourly Rate:</span>
                    <span className="font-medium">₹{workCenter.hourlyRate}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-3 border-t">
                  <button
                    onClick={() => toggleStatusMutation.mutate(workCenter.id)}
                    className={`flex items-center gap-1 px-2 py-1 rounded text-sm ${
                      workCenter.isActive
                        ? 'text-green-600 hover:bg-green-50'
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {workCenter.isActive ? (
                      <CheckCircleIcon className="h-4 w-4" />
                    ) : (
                      <XCircleIcon className="h-4 w-4" />
                    )}
                    {workCenter.isActive ? 'Active' : 'Inactive'}
                  </button>

                  <div className="flex gap-2">
                    <button
                      onClick={() => openModal(workCenter)}
                      title="Edit"
                      aria-label="Edit"
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      <PencilIcon className="h-4 w-4 text-gray-600" />
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm('Are you sure you want to delete this work center?')) {
                          deleteMutation.mutate(workCenter.id);
                        }
                      }}
                      title="Delete"
                      aria-label="Delete"
                      className="p-1 hover:bg-red-50 rounded"
                    >
                      <TrashIcon className="h-4 w-4 text-red-600" />
                    </button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <WorkCenterModal
          workCenter={selectedWorkCenter}
          plants={plantsData?.data || []}
          costCenters={costCentersData?.data || []}
          onClose={closeModal}
          onSubmit={(data) => createUpdateMutation.mutate(data)}
        />
      )}
    </div>
  );
};

// Work Center Modal Component
const WorkCenterModal: React.FC<{
  workCenter: WorkCenter | null;
  plants: Plant[];
  costCenters: CostCenter[];
  onClose: () => void;
  onSubmit: (data: any) => void;
}> = ({ workCenter, plants, costCenters, onClose, onSubmit }) => {
  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: workCenter || {
      capacity: 1,
      standardHoursPerDay: 8,
      efficiencyPercent: 85,
      utilizationPercent: 90,
      hourlyRate: 0,
      setupCost: 0,
      overheadRate: 0,
      isActive: true,
      status: WorkCenterStatus.ACTIVE,
      type: WorkCenterType.MACHINE,
    },
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">
          {workCenter ? 'Edit Work Center' : 'Add New Work Center'}
        </h2>
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Code *
              </label>
              <input
                {...register('code', { required: 'Code is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              {errors.code && (
                <p className="text-red-500 text-sm mt-1">{errors.code.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name *
              </label>
              <input
                {...register('name', { required: 'Name is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type *
              </label>
              <select
                {...register('type', { required: 'Type is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {Object.entries(WorkCenterType).map(([key, value]) => (
                  <option key={value} value={value}>
                    {key.charAt(0) + key.slice(1).toLowerCase()}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Plant *
              </label>
              <select
                {...register('plantId', { required: 'Plant is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select Plant</option>
                {plants.map((plant) => (
                  <option key={plant.id} value={plant.id}>
                    {plant.name} ({plant.code})
                  </option>
                ))}
              </select>
              {errors.plantId && (
                <p className="text-red-500 text-sm mt-1">{errors.plantId.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cost Center
              </label>
              <select
                {...register('costCenterId')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Select Cost Center</option>
                {costCenters.map((cc) => (
                  <option key={cc.id} value={cc.id}>
                    {cc.name} ({cc.code})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                {...register('status')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {Object.entries(WorkCenterStatus).map(([key, value]) => (
                  <option key={value} value={value}>
                    {key.charAt(0) + key.slice(1).toLowerCase()}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location
              </label>
              <input
                {...register('location')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Capacity
              </label>
              <input
                type="number"
                {...register('capacity', { valueAsNumber: true })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Standard Hours/Day
              </label>
              <input
                type="number"
                step="0.01"
                {...register('standardHoursPerDay', { valueAsNumber: true })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Efficiency %
              </label>
              <input
                type="number"
                step="0.01"
                {...register('efficiencyPercent', { valueAsNumber: true })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Utilization %
              </label>
              <input
                type="number"
                step="0.01"
                {...register('utilizationPercent', { valueAsNumber: true })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hourly Rate (₹)
              </label>
              <input
                type="number"
                step="0.01"
                {...register('hourlyRate', { valueAsNumber: true })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Setup Cost (₹)
              </label>
              <input
                type="number"
                step="0.01"
                {...register('setupCost', { valueAsNumber: true })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Overhead Rate (₹)
              </label>
              <input
                type="number"
                step="0.01"
                {...register('overheadRate', { valueAsNumber: true })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              {...register('description')}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              {...register('notes')}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              {...register('isActive')}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <label className="text-sm text-gray-700">Active</label>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">
              {workCenter ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WorkCenterManagement;